import React, { useEffect, useState } from 'react';
import {
  TextField,
  Button,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Avatar,
  Typography,
} from '@mui/material';

interface ExpertFormProps {
  initialValues?: any;
  onSubmit: (data: any) => void;
  loading?: boolean;
}

interface FormData {
  profile_picture: File | null;
  resume: File | null;
  first_name: string;
  middle_name: string;
  last_name: string;
  date_of_birth: string;
  address: string;
  phone_number: string;
  email: string;
  job_title: string;
  gender: string;
  password?: string;
  role?: string;
}

const ExpertForm: React.FC<ExpertFormProps> = ({
  initialValues = {},
  onSubmit,
  loading,
}) => {
  const [form, setForm] = useState<FormData>({
    profile_picture: null,
    resume: null,
    first_name: '',
    middle_name: '',
    last_name: '',
    date_of_birth: '',
    address: '',
    phone_number: '',
    email: '',
    job_title: '',
    gender: '',
    password: '',
    role: 'expert_user',
    ...initialValues,
  });
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [resumeFileName, setResumeFileName] = useState<string>('');

  useEffect(() => {
    if (initialValues) {
      setForm((prev: FormData) => ({
        ...prev,
        ...initialValues,
        date_of_birth: initialValues.date_of_birth
          ? new Date(initialValues.date_of_birth).toISOString().split('T')[0]
          : '',
      }));
      if (initialValues.profile_picture) {
        setPreviewUrl(initialValues.profile_picture);
      }
      if (initialValues.resume_file_path) {
        setResumeFileName(
          initialValues.resume_file_path.split('/').pop() || ''
        );
      }
    }
  }, [initialValues]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (e: any) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setForm({ ...form, profile_picture: file });
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    }
  };

  const handleResumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setForm({ ...form, resume: file });
      setResumeFileName(file.name);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Create FormData for file upload
    const formData = new FormData();

    if (form.profile_picture) {
      formData.append('profile_picture', form.profile_picture);
    }

    if (form.resume) {
      formData.append('resume', form.resume);
    }

    formData.append('first_name', form.first_name);
    formData.append('middle_name', form.middle_name);
    formData.append('last_name', form.last_name);
    formData.append('date_of_birth', form.date_of_birth);
    formData.append('address', form.address);
    formData.append('phone_number', form.phone_number);
    formData.append('email', form.email);
    formData.append('job_title', form.job_title);
    formData.append('gender', form.gender);
    if (form.password) formData.append('password', form.password);
    if (form.role) formData.append('role', form.role);

    onSubmit(formData);
  };

  return (
    <Box
      component='form'
      onSubmit={handleSubmit}
      sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: 500 }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Avatar
          src={previewUrl}
          alt='Profile Preview'
          sx={{ width: 80, height: 80 }}
        >
          {form.first_name?.charAt(0) || 'E'}
        </Avatar>
        <Button variant='outlined' component='label' sx={{ flex: 1 }}>
          Upload Profile Picture
          <input
            type='file'
            hidden
            accept='image/*'
            onChange={handleFileChange}
          />
        </Button>
      </Box>

      <Box sx={{ display: 'flex', gap: 2 }}>
        <TextField
          label='First Name'
          name='first_name'
          value={form.first_name}
          onChange={handleChange}
          required
          fullWidth
        />
        <TextField
          label='Middle Name'
          name='middle_name'
          value={form.middle_name}
          onChange={handleChange}
          fullWidth
        />
        <TextField
          label='Last Name'
          name='last_name'
          value={form.last_name}
          onChange={handleChange}
          required
          fullWidth
        />
      </Box>

      <TextField
        label='Date of Birth'
        name='date_of_birth'
        type='date'
        value={form.date_of_birth}
        onChange={handleChange}
        required
        fullWidth
        InputLabelProps={{ shrink: true }}
      />

      <TextField
        label='Address'
        name='address'
        value={form.address}
        onChange={handleChange}
        multiline
        rows={3}
        required
        fullWidth
      />

      <Box sx={{ display: 'flex', gap: 2 }}>
        <TextField
          label='Phone Number'
          name='phone_number'
          value={form.phone_number}
          onChange={handleChange}
          required
          fullWidth
        />
        <TextField
          label='Email'
          name='email'
          type='email'
          value={form.email}
          onChange={handleChange}
          required
          fullWidth
        />
      </Box>

      <TextField
        label='Job Title'
        name='job_title'
        value={form.job_title}
        onChange={handleChange}
        fullWidth
      />

      <FormControl fullWidth>
        <InputLabel>Gender</InputLabel>
        <Select
          name='gender'
          value={form.gender}
          onChange={handleSelectChange}
          input={<OutlinedInput label='Gender' />}
        >
          <MenuItem value=''>None</MenuItem>
          <MenuItem value='male'>Male</MenuItem>
          <MenuItem value='female'>Female</MenuItem>
          <MenuItem value='other'>Other</MenuItem>
        </Select>
      </FormControl>

      <Box sx={{ display: 'flex', gap: 2 }}>
        <TextField
          label='Password (optional)'
          name='password'
          type='password'
          value={form.password || ''}
          onChange={handleChange}
          fullWidth
          helperText='Leave blank to auto-generate'
        />
        <FormControl fullWidth>
          <InputLabel>Role</InputLabel>
          <Select
            name='role'
            value={form.role || 'expert_user'}
            onChange={handleSelectChange}
            input={<OutlinedInput label='Role' />}
          >
            <MenuItem value='expert_user'>Expert User</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Button variant='outlined' component='label' fullWidth>
          Upload Resume (PDF, DOC, DOCX)
          <input
            type='file'
            hidden
            accept='.pdf,.doc,.docx'
            onChange={handleResumeChange}
          />
        </Button>
        {resumeFileName && (
          <Typography variant='caption' color='textSecondary'>
            Selected: {resumeFileName}
          </Typography>
        )}
      </Box>

      <Button type='submit' variant='contained' disabled={loading} fullWidth>
        {loading ? 'Submitting...' : 'Submit'}
      </Button>
    </Box>
  );
};

export default ExpertForm;
